/**
 * Secure Token Storage Utility
 *
 * SECURITY NOTE: This utility provides a centralized token storage mechanism.
 * While localStorage is used for persistence, this wrapper enables future
 * migration to more secure storage methods (httpOnly cookies, memory-only, etc.)
 *
 * Current limitations (documented for security audit):
 * - localStorage is vulnerable to XSS attacks
 * - Consider upgrading to httpOnly cookies for production
 *
 * Mitigations in place:
 * - CSP headers via helmet.js prevent inline scripts
 * - JWT tokens have 24h expiration
 * - Session tracking allows session revocation
 */

const TOKEN_KEY = 'token';
const TOKEN_TIMESTAMP_KEY = 'token_timestamp';

/**
 * Store the authentication token
 * @param token - JWT token string
 */
export function setToken(token: string): void {
  try {
    // Store token and timestamp
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Failed to store token:', error);
    // Fallback to memory-only storage (will be lost on page refresh)
    (window as any).__AUTH_TOKEN__ = token;
  }
}

/**
 * Retrieve the authentication token
 * @returns Token string or null if not found
 */
export function getToken(): string | null {
  try {
    // Check localStorage first
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      return token;
    }
    // Fallback to memory storage
    return (window as any).__AUTH_TOKEN__ || null;
  } catch (error) {
    console.error('Failed to retrieve token:', error);
    return (window as any).__AUTH_TOKEN__ || null;
  }
}

/**
 * Remove the authentication token (logout)
 */
export function removeToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Failed to remove token:', error);
  }
  // Always clear memory storage
  (window as any).__AUTH_TOKEN__ = null;
}

/**
 * Check if user is authenticated
 * @returns boolean indicating if a token exists
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * Get token age in milliseconds
 * @returns Token age or null if no token
 */
export function getTokenAge(): number | null {
  try {
    const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
    if (timestamp) {
      return Date.now() - parseInt(timestamp, 10);
    }
  } catch {
    // Ignore errors
  }
  return null;
}

/**
 * Check if token might be expired (client-side check)
 * Note: Server always validates token expiration
 * @param maxAgeMs - Maximum token age in milliseconds (default: 23 hours)
 */
export function isTokenPossiblyExpired(maxAgeMs: number = 23 * 60 * 60 * 1000): boolean {
  const age = getTokenAge();
  if (age === null) return true;
  return age > maxAgeMs;
}

/**
 * Get authorization header value
 * @returns Bearer token header value or empty string
 */
export function getAuthHeader(): string {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
}

/**
 * Get authorization headers object
 * @returns Headers object with Authorization header if token exists
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Clear all authentication data (for security purposes)
 */
export function clearAllAuthData(): void {
  removeToken();
  // Clear any other auth-related storage
  try {
    // Clear any potential session data
    sessionStorage.clear();
  } catch {
    // Ignore errors
  }
}

// Export for testing/debugging
export const __tokenStorage = {
  TOKEN_KEY,
  TOKEN_TIMESTAMP_KEY
};
